// Pure helpers for the File Upload → Tables tab (GET /upload/tables). No React here so they
// can be unit-tested directly. The backend owns every decision (types come from the stored
// upload classification); these only shape the response for display.

export const TYPE_BADGES = {
  number: { label: "Number", className: "s3upload-type-badge number" },
  date: { label: "Date", className: "s3upload-type-badge date" },
  currency: { label: "Currency", className: "s3upload-type-badge currency" },
  percent: { label: "Percent", className: "s3upload-type-badge percent" },
  text: { label: "Text", className: "s3upload-type-badge text" },
};

export function typeBadge(type) {
  return TYPE_BADGES[type] || TYPE_BADGES.text;
}

const arr = (v) => (Array.isArray(v) ? v : []);

// Fill safe defaults so the view never crashes on a partial/odd response.
export function normalizeTableStructure(resp) {
  const r = resp && typeof resp === "object" ? resp : {};
  return {
    project_id: r.project_id || "",
    tables: arr(r.tables).map((t) => ({
      ...t,
      category: (t && t.category) || "",
      physical_table: (t && t.physical_table) || "",
      description: (t && t.description) || "",
      note: (t && t.note) || "",
      file_count: Number((t && t.file_count) || 0),
      last_upload: (t && t.last_upload) || "",
      files: arr(t && t.files),
      fields: arr(t && t.fields).map((f) => ({
        ...f,
        name: (f && f.name) || "",
        type: (f && f.type) || "text",
        format: (f && f.format) || null,
        source_headers: arr(f && f.source_headers),
      })),
    })),
    archived_only: arr(r.archived_only),
    uncategorized_file_count: Number(r.uncategorized_file_count || 0),
  };
}

export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Per-type wording for the summary strip ("12 dates · 30 numbers · 8 currency").
const TYPE_SUMMARY_WORDS = {
  date: ["date", "dates"],
  number: ["number", "numbers"],
  currency: ["currency", "currency"],
  percent: ["percent", "percent"],
  text: ["text", "text"],
};
const TYPE_ORDER = ["date", "number", "currency", "percent", "text"];

// Totals for the summary strip, derived only from the (normalized) response.
export function summarizeTables(tables) {
  const byType = { date: 0, number: 0, currency: 0, percent: 0, text: 0 };
  let fields = 0;
  let files = 0;
  arr(tables).forEach((t) => {
    files += Number((t && t.file_count) || 0);
    arr(t && t.fields).forEach((f) => {
      fields += 1;
      const ty = f && TYPE_SUMMARY_WORDS[f.type] ? f.type : "text";
      byType[ty] += 1;
    });
  });
  return { tables: arr(tables).length, fields, files, byType };
}

export function summaryText(s) {
  return [plural(s.tables, "table", "tables"), plural(s.fields, "field", "fields"), plural(s.files, "file", "files")].join(" · ");
}

export function typeSummaryText(s) {
  return TYPE_ORDER.filter((t) => s.byType[t] > 0)
    .map((t) => plural(s.byType[t], TYPE_SUMMARY_WORDS[t][0], TYPE_SUMMARY_WORDS[t][1]))
    .join(" · ");
}

const norm = (v) => String(v == null ? "" : v).toLowerCase();

// Field search: case-insensitive substring over the clean name OR any original header. Returns the
// tables that still have matching fields (with only those fields). An empty/blank query returns
// every table unchanged. Plain loops: fine for ~100 tables x ~50 fields per keystroke.
export function filterTableStructure(tables, query) {
  const list = arr(tables);
  const q = norm(query).trim();
  if (!q) {
    const fieldCount = list.reduce((n, t) => n + arr(t && t.fields).length, 0);
    return { query: "", tables: list, fieldCount, tableCount: list.length };
  }
  const out = [];
  let fieldCount = 0;
  list.forEach((t) => {
    const fields = arr(t && t.fields).filter((f) =>
      norm(f && f.name).includes(q) || arr(f && f.source_headers).some((h) => norm(h).includes(q)));
    if (fields.length) {
      out.push({ ...t, fields });
      fieldCount += fields.length;
    }
  });
  return { query: q, tables: out, fieldCount, tableCount: out.length };
}

// Split text into [{text, match}] parts around every case-insensitive occurrence of query, so the
// view can wrap matches in <mark> without dangerouslySetInnerHTML.
export function highlightParts(text, query) {
  const s = String(text == null ? "" : text);
  const q = norm(query).trim();
  if (!q || !s) return [{ text: s, match: false }];
  const lower = s.toLowerCase();
  const parts = [];
  let i = 0;
  let at = lower.indexOf(q, i);
  while (at !== -1) {
    if (at > i) parts.push({ text: s.slice(i, at), match: false });
    parts.push({ text: s.slice(at, at + q.length), match: true });
    i = at + q.length;
    at = lower.indexOf(q, i);
  }
  if (i < s.length) parts.push({ text: s.slice(i), match: false });
  return parts;
}

// Before -> after display for one field. Headers identical to the clean name are redundant and
// dropped; if nothing is left there's no arrow. Up to `max` headers are shown, the rest become
// "+N" (all listed in `title`). Headers matching the search query are listed first so a match
// is never hidden behind "+N".
export function fieldHeaderDisplay(field, query = "", max = 2) {
  const name = String((field && field.name) || "");
  const headers = arr(field && field.source_headers)
    .map((h) => String(h == null ? "" : h))
    .filter((h) => h.trim() && h.trim() !== name);
  if (!headers.length) return { showArrow: false, shown: [], extra: 0, title: "" };
  const q = norm(query).trim();
  const ordered = q
    ? [...headers.filter((h) => norm(h).includes(q)), ...headers.filter((h) => !norm(h).includes(q))]
    : headers;
  const shown = ordered.slice(0, max);
  return { showArrow: true, shown, extra: ordered.length - shown.length, title: headers.join(", ") };
}

// ── Tree grid ────────────────────────────────────────────────────────────────────────────────
// Flatten tables → rows for the tree grid. Only OPEN tables contribute child (field) rows, so a
// collapsed 100 x 50 project renders 100 rows, not 5,000.
export function buildTreeRows(tables, isOpen) {
  const rows = [];
  arr(tables).forEach((t) => {
    const open = Boolean(isOpen && isOpen(t.category));
    rows.push({ kind: "table", key: `t:${t.category}`, table: t, open });
    if (open) arr(t.fields).forEach((f) => rows.push({ kind: "field", key: `f:${t.category}:${f.name}`, table: t, field: f }));
  });
  return rows;
}

// Expand all / Collapse all. Outside search the open set is the user's own choice (`openCats`);
// while searching every matching table is open unless the user closed it (`searchCollapsed`).
// Returns which state to replace and its new value.
export function bulkToggleState(categories, expand, searching) {
  const cats = arr(categories);
  if (searching) return { key: "searchCollapsed", value: new Set(expand ? [] : cats) };
  return { key: "openCats", value: new Set(expand ? cats : []) };
}

// ── Relationships (shared field names) ──────────────────────────────────────────────────────
// Boomerang relates tables by SHARED CLEAN FIELD NAMES: the same name in 2+ tables is a join
// candidate the AI can use. These are matches by name, not enforced foreign keys. Deterministic,
// computed from the /upload/tables response only.

// Generic date/time names: shared by almost every ad table, so they say little about how two
// tables relate. Hidden by default in the Relationships view (never removed; one click shows them).
export const GENERIC_DATE_FIELDS = new Set([
  "date", "day", "week", "month", "year", "quarter", "hour", "time", "timestamp", "datetime",
  "report_date", "reporting_date", "start_date", "end_date", "date_start", "date_stop", "date_end",
  "week_start", "week_start_date", "month_start", "reporting_starts", "reporting_ends",
  "created_at", "updated_at",
]);

export function isGenericDateField(name) {
  return GENERIC_DATE_FIELDS.has(norm(name).trim());
}

// [{name, type, types, tables, count, headers}] for every clean field name in 2+ tables. `tables`
// keeps response order; `type` is the most frequent type (ties → TYPE_ORDER), `types` the distinct
// ones (more than one = mixed); `headers` = every original header seen for it. Sorted by number of
// tables desc, then name.
export function computeSharedFields(tables) {
  const byName = new Map();
  arr(tables).forEach((t) => {
    const cat = (t && t.category) || "";
    arr(t && t.fields).forEach((f) => {
      const name = String((f && f.name) || "").trim();
      if (!name) return;
      let e = byName.get(name);
      if (!e) { e = { name, tables: [], typeCounts: {}, headers: new Set() }; byName.set(name, e); }
      if (!e.tables.includes(cat)) e.tables.push(cat);
      const ty = (f && TYPE_BADGES[f.type]) ? f.type : "text";
      e.typeCounts[ty] = (e.typeCounts[ty] || 0) + 1;
      arr(f && f.source_headers).forEach((h) => { if (String(h || "").trim()) e.headers.add(String(h)); });
    });
  });
  const out = [];
  byName.forEach((e) => {
    if (e.tables.length < 2) return;
    const types = TYPE_ORDER.filter((ty) => e.typeCounts[ty]);
    const type = types.reduce((best, ty) => (e.typeCounts[ty] > e.typeCounts[best] ? ty : best), types[0]);
    out.push({ name: e.name, type, types, tables: e.tables, count: e.tables.length, headers: [...e.headers] });
  });
  out.sort((a, b) => b.count - a.count || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  return out;
}

// Measure types (by the shared field's majority type). A shared measure (spend, cpm, clicks) lets
// the AI compare or combine tables, but it is not a key to join them on.
const MEASURE_TYPES = new Set(["number", "currency", "percent"]);
// Identifier names (campaign_id, ad_key, site_code, id) are keys even when the values are numeric.
const IDENTIFIER_NAME = /(^|_)(id|ids|key|code|uuid|guid)$/;

export function isSharedMetric(entry) {
  return Boolean(entry && MEASURE_TYPES.has(entry.type) && !IDENTIFIER_NAME.test(norm(entry.name).trim()));
}

// Why a shared field is "common" (low signal), or null: a generic date/time name, or (3+ tables) a
// MEASURE present in every table. Text/ID dimensions in every table (campaign_id, campaign_name)
// are the headline join keys, so they always stay visible.
export function commonFieldReason(entry, tableCount) {
  if (!entry) return null;
  if (isGenericDateField(entry.name)) return "date";
  if (tableCount >= 3 && entry.count >= tableCount && isSharedMetric(entry)) return "all";
  return null;
}

// Split shared fields into the useful join keys and the common ones the default view hides.
export function partitionSharedFields(shared, tableCount) {
  const useful = [];
  const common = [];
  arr(shared).forEach((e) => (commonFieldReason(e, tableCount) ? common : useful).push(e));
  return { useful, common };
}

// Relationships search: field name, any original header, or a table name (case-insensitive).
export function filterSharedFields(shared, query) {
  const q = norm(query).trim();
  if (!q) return arr(shared);
  return arr(shared).filter((e) => norm(e.name).includes(q)
    || arr(e.headers).some((h) => norm(h).includes(q))
    || arr(e.tables).some((c) => norm(c).includes(q)));
}

// name → shared entry, for the "in N tables" indicator on Structure field rows.
export function sharedFieldIndex(shared) {
  const m = new Map();
  arr(shared).forEach((e) => m.set(e.name, e));
  return m;
}

// Refresh key for the Tables view: changes whenever the file list changes (upload completes,
// archive/restore/move, background refresh), so the tree refetches without extra wiring.
export function filesSignature(activeFiles, archivedFiles) {
  const a = arr(activeFiles);
  const b = arr(archivedFiles);
  let latest = "";
  [...a, ...b].forEach((f) => {
    const m = (f && f.last_modified) || "";
    if (m > latest) latest = m;
  });
  return `${a.length}:${b.length}:${latest}`;
}
